import { faThumbsUp, faThumbsDown } from '@fortawesome/free-regular-svg-icons'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, ButtonGroup, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import type React from 'react'

import type { FlagFeedbackPayload } from '@/lib/apollo/mutation/patchFlagFeedback/mutation'
import type { VerifAIFlag } from '@/lib/apollo/query/getVerifAIFlags/query'

import { FeedbackDialog } from './modal'
import type { FeedbackType } from './types'
import { getSelectedFeedbackFromUser } from './utils'

export interface Props {
  flag: VerifAIFlag
  userId: string
  subjectId: string
  submitFeedback: (payload: FlagFeedbackPayload) => Promise<void>
  loadingFeedback?: boolean
  feedbackError?: Error
}

export const Feedback = ({
  flag,
  userId,
  subjectId,
  feedbackError,
  loadingFeedback,
  submitFeedback,
}: Props) => {
  const [activeFeedbackType, setActiveFeedbackType] = useState<FeedbackType | null>(null)
  const flagFeedbackFromUser = getSelectedFeedbackFromUser(flag.feedback, userId)

  const closeDialog = () => setActiveFeedbackType(null)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const comment = (formData.get('comment') as string) || undefined

    submitFeedback({
      serviceName: flag.serviceName,
      helpful: activeFeedbackType === 'positive',
      comment,
      userId,
      subjectId,
    }).then(closeDialog)
  }

  return (
    <Stack
      direction={'row'}
      justifyContent={'flex-end'}
      alignItems={'center'}
      spacing={1}
    >
      <Typography
        variant="body2"
        fontStyle="italic"
        color={feedbackError !== undefined ? 'error' : 'textSecondary'}
      >
        {loadingFeedback ? (
          <FontAwesomeIcon size="1x" icon={faSpinner} spin={true} />
        ) : feedbackError !== undefined ? (
          'Unable to submit feedback.'
        ) : flagFeedbackFromUser !== undefined ? (
          'Thank you for your feedback!'
        ) : (
          'Was this information helpful?'
        )}
      </Typography>
      <ButtonGroup variant="outlined" aria-label="Thumbs up and down feedback buttons">
        <Button
          aria-label="Thumbs up"
          onClick={() => setActiveFeedbackType('positive')}
          style={{
            backgroundColor:
              flagFeedbackFromUser?.helpful === true ? '#d1e7dd' : 'transparent',
          }}
        >
          <FontAwesomeIcon size="1x" icon={faThumbsUp} color={'#082842'} />
        </Button>
        <Button
          aria-label="Thumbs down"
          onClick={() => setActiveFeedbackType('negative')}
          style={{
            backgroundColor:
              flagFeedbackFromUser?.helpful === false ? '#f8d7da' : 'transparent',
          }}
        >
          <FontAwesomeIcon size="1x" icon={faThumbsDown} color={'#082842'} />
        </Button>
      </ButtonGroup>
      <FeedbackDialog
        isOpen={activeFeedbackType !== null}
        isLoading={loadingFeedback}
        feedbackType={activeFeedbackType}
        onClose={closeDialog}
        onSubmit={handleSubmit}
      />
    </Stack>
  )
}
